import { Banknote, Smartphone, Wallet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';

type Step = { title: string; body: string };

const STEPS: ReadonlyArray<Step> = [
  {
    title: 'Retrouve la vente',
    body: "Ouvre la vente d'origine et sélectionne les articles à reprendre — tout ou partie. Velko connaît déjà les prix et les quantités vendues.",
  },
  {
    title: 'Retour, échange ou avoir',
    body: 'Choisis : rembourser, échanger contre un autre article (le différentiel est calculé), ou créditer le compte du client. Le stock se met à jour selon ton choix.',
  },
  {
    title: 'Le gérant valide si besoin',
    body: "En dessous du seuil, le retour est immédiat. Au-dessus, il part en validation : aucun argent ne sort tant que le gérant n'a pas approuvé.",
  },
  {
    title: 'Ticket & historique',
    body: 'Le reçu de retour s\'imprime, le statut de la vente passe à « remboursé » si elle est rendue en entier, et tout reste dans l\'historique.',
  },
];

type Mode = { Icon: LucideIcon; title: string; body: string };

const MODES: ReadonlyArray<Mode> = [
  {
    Icon: Banknote,
    title: 'Espèces',
    body: 'Remboursement direct depuis le tiroir-caisse, déduit du fond de caisse.',
  },
  {
    Icon: Smartphone,
    title: 'Mobile Money',
    body: 'Tu saisis la référence de la transaction (Orange Money, MTN MoMo, Wave) pour garder la trace.',
  },
  {
    Icon: Wallet,
    title: 'Avoir client',
    body: 'Le montant est crédité sur le compte du client, utilisable sur ses prochains achats.',
  },
];

export default function RetoursHowItWorksV2() {
  return (
    <section className="bg-paper py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <SectionLabel>Comment ça marche</SectionLabel>
        <h2 className="mt-3 max-w-3xl font-marketing-display text-[clamp(30px,4.4vw,56px)] font-bold leading-[1.02] tracking-[-0.035em] text-charcoal-800">
          Quatre étapes. <span className="underline-lime">Trois modes de remboursement.</span>
        </h2>

        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step, i) => (
            <div
              key={step.title}
              className="flex flex-col gap-4 border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-4"
            >
              <div className="flex items-center gap-3">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-charcoal-800 bg-lime-500 font-marketing-display text-[18px] font-bold text-charcoal-800"
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                {i < STEPS.length - 1 && (
                  <span className="hidden h-px flex-1 bg-charcoal-200 lg:block" aria-hidden="true" />
                )}
              </div>
              <h3 className="font-marketing-display text-[18px] font-bold leading-[1.18] tracking-[-0.01em] text-charcoal-800">
                {step.title}
              </h3>
              <p className="font-marketing-body text-[14px] leading-[1.55] text-charcoal-600">
                {step.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-5 grid gap-5 md:grid-cols-3">
          {MODES.map(({ Icon, title, body }) => (
            <div
              key={title}
              className="flex items-start gap-4 border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-4"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center border-2 border-charcoal-800 bg-lime-500 text-charcoal-800">
                <Icon className="h-[20px] w-[20px]" strokeWidth={2} aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-marketing-display text-[17px] font-bold leading-[1.15] tracking-[-0.01em] text-charcoal-800">
                  {title}
                </h3>
                <p className="mt-1.5 font-marketing-body text-[14px] leading-[1.5] text-charcoal-600">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
